/**
 * Admin Predictions Table Component
 * A simplified predictions table for admin operations with bulk actions
 */
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Alert,
} from '@mui/material';
import {
  Visibility,
  // Delete,
  Edit,
  GetApp,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { predictionsApi } from '../../services/predictions.service';
import { LessonV1_SpineTagsPrediction } from '../../types';
import { useAppStore } from '../../store/appStore';
import { useNavigate } from 'react-router-dom';

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    case 'in_progress':
      return 'warning';
    default:
      return 'default';
  }
};

export default function AdminPredictionsTable() {
  const { selectedProduct, selectedExperiment } = useAppStore();
  const navigate = useNavigate();

  const {
    data: predictions,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['predictions', selectedProduct, selectedExperiment, 'admin'],
    queryFn: () =>
      predictionsApi.list(selectedProduct!, selectedExperiment!, {
        pageSize: 100, // Show more records for admin
      }),
    enabled: !!(selectedProduct && selectedExperiment),
  });

  const handleViewDetails = (prediction: LessonV1_SpineTagsPrediction) => {
    navigate(`/detail/${prediction.id}`, { state: { prediction } });
  };

  // const handleDeleteRecord = (predictionId: string) => {
  //   // TODO: Implement delete functionality
  //   console.log('Delete record:', predictionId);
  // };

  const handleBulkExport = () => {
    // TODO: Implement bulk export functionality
    console.log('Bulk export');
  };

  if (!selectedProduct || !selectedExperiment) {
    return (
      <Alert severity="info">
        Please select a product and experiment from the main page to view predictions.
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Error loading predictions: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Predictions Table
        </Typography>
        <Button
          variant="contained"
          startIcon={<GetApp />}
          onClick={handleBulkExport}
          disabled={!predictions?.items?.length}
        >
          Export All
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography>Loading predictions...</Typography>
        </Box>
      ) : (
        <>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Showing {predictions?.items?.length || 0} predictions for{' '}
            <strong>{selectedProduct}</strong> -{' '}
            <strong>{selectedExperiment}</strong>
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Lesson</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Grade</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reviewer</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {predictions?.items?.map((prediction) => (
                  <TableRow key={prediction.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {prediction.lesson_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {prediction.id}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={prediction.lesson_subject}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{prediction.lesson_grade}</TableCell>
                    <TableCell>{prediction.unit_number}</TableCell>
                    <TableCell>
                      <Chip
                        label={prediction.review_status}
                        size="small"
                        color={getStatusColor(prediction.review_status) as any}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {prediction.assignee || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {prediction.updated_at
                          ? new Date(prediction.updated_at).toLocaleDateString()
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(prediction)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(prediction)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteRecord(prediction.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip> */}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {!predictions?.items?.length && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="textSecondary">
                No predictions found for the selected product and experiment.
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}